import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { useKitchenOrders } from '@/features/pos/hooks/useKitchenOrders';
import { Clock, CheckCircle, ChefHat, Loader2 } from 'lucide-react';
import { format, differenceInMinutes } from 'date-fns';
import { fr } from 'date-fns/locale';

export const Route = createFileRoute('/apps/pos/display/')({
  component: CustomerDisplayPage,
});

function CustomerDisplayPage() {
  const { data: orders = [], isLoading } = useKitchenOrders();
  const [currentTime, setCurrentTime] = useState('');

  useEffect(() => {
    const updateTime = () => {
      setCurrentTime(format(new Date(), 'HH:mm:ss'));
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  // Group orders by status
  const newOrders = orders.filter(o => o.status === 'pending');
  const preparingOrders = orders.filter(o => o.status === 'confirmed' || o.status === 'preparing');
  const readyOrders = orders.filter(o => o.status === 'ready');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50">
        <Loader2 className="w-12 h-12 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      {/* Header */}
      <div className="bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center">
                <ChefHat className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Statut des Commandes
                </h1>
                <p className="text-gray-600">Suivez votre commande en temps réel</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-4xl font-bold text-emerald-600">{currentTime}</p>
              <p className="text-sm text-gray-600">
                {format(new Date(), 'EEEE d MMMM yyyy', { locale: fr })}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-8 py-8">
        {/* Status Summary */}
        <div className="grid grid-cols-3 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-2 border-yellow-300 p-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-400 rounded-full mx-auto mb-4 flex items-center justify-center">
                <Clock className="w-8 h-8 text-yellow-900" />
              </div>
              <p className="text-5xl font-bold text-yellow-900 mb-2">{newOrders.length}</p>
              <p className="text-lg font-semibold text-yellow-800">En Attente</p>
            </div>
          </Card>

          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-300 p-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-400 rounded-full mx-auto mb-4 flex items-center justify-center">
                <ChefHat className="w-8 h-8 text-blue-900" />
              </div>
              <p className="text-5xl font-bold text-blue-900 mb-2">{preparingOrders.length}</p>
              <p className="text-lg font-semibold text-blue-800">En Préparation</p>
            </div>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-300 p-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-400 rounded-full mx-auto mb-4 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-900" />
              </div>
              <p className="text-5xl font-bold text-green-900 mb-2">{readyOrders.length}</p>
              <p className="text-lg font-semibold text-green-800">Prêtes!</p>
            </div>
          </Card>
        </div>

        {/* Orders Display */}
        {orders.length === 0 ? (
          <div className="text-center py-20">
            <CheckCircle className="w-24 h-24 text-emerald-400 mx-auto mb-6" />
            <p className="text-3xl text-gray-600 font-semibold">Aucune commande en cours</p>
            <p className="text-gray-500 mt-2 text-lg">Nous sommes prêts à vous servir!</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Ready Orders - Most Important */}
            {readyOrders.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-green-700 mb-4 flex items-center gap-3">
                  <CheckCircle className="w-8 h-8" />
                  Commandes Prêtes - À Récupérer
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {readyOrders.map(order => (
                    <OrderDisplayCard
                      key={order.id}
                      order={order}
                      status="ready"
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Preparing Orders */}
            {preparingOrders.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-blue-700 mb-4 flex items-center gap-3">
                  <ChefHat className="w-8 h-8" />
                  En Préparation
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {preparingOrders.map(order => (
                    <OrderDisplayCard
                      key={order.id}
                      order={order}
                      status="preparing"
                    />
                  ))}
                </div>
              </div>
            )}

            {/* New Orders */}
            {newOrders.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-yellow-700 mb-4 flex items-center gap-3">
                  <Clock className="w-8 h-8" />
                  En Attente
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {newOrders.map(order => (
                    <OrderDisplayCard
                      key={order.id}
                      order={order}
                      status="new"
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Animated Background Elements */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden -z-10">
        <div className="absolute top-20 left-10 w-32 h-32 bg-emerald-200 rounded-full opacity-20 animate-pulse"></div>
        <div className="absolute top-40 right-20 w-40 h-40 bg-teal-200 rounded-full opacity-20 animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-20 left-1/4 w-36 h-36 bg-cyan-200 rounded-full opacity-20 animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>
    </div>
  );
}

function OrderDisplayCard({ order, status }: { order: any; status: 'new' | 'preparing' | 'ready' }) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(differenceInMinutes(new Date(), new Date(order.created_at)));
    }, 1000);
    return () => clearInterval(interval);
  }, [order.created_at]);

  const getColorClasses = () => {
    switch (status) {
      case 'ready':
        return 'bg-gradient-to-br from-green-100 to-green-200 border-green-400 shadow-green-200';
      case 'preparing':
        return 'bg-gradient-to-br from-blue-100 to-blue-200 border-blue-400 shadow-blue-200';
      default:
        return 'bg-gradient-to-br from-yellow-100 to-yellow-200 border-yellow-400 shadow-yellow-200';
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'ready':
        return <CheckCircle className="w-8 h-8 text-green-700" />;
      case 'preparing':
        return <ChefHat className="w-8 h-8 text-blue-700" />;
      default:
        return <Clock className="w-8 h-8 text-yellow-700" />;
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'ready':
        return 'PRÊTE!';
      case 'preparing':
        return 'En préparation...';
      default:
        return 'En attente...';
    }
  };

  return (
    <Card className={`border-4 ${getColorClasses()} shadow-xl transform hover:scale-105 transition-transform`}>
      <div className="p-6 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-3xl font-bold text-gray-900">
              {order.table_id || 'Commande'}
            </h3>
            <p className="text-sm text-gray-700 font-medium">#{order.order_number.slice(-4)}</p>
          </div>
          {getStatusIcon()}
        </div>

        {/* Status Badge */}
        <div className={`text-center py-3 rounded-lg font-bold text-lg ${
          status === 'ready' ? 'bg-green-500 text-white animate-pulse' :
          status === 'preparing' ? 'bg-blue-500 text-white' :
          'bg-yellow-500 text-white'
        }`}>
          {getStatusText()}
        </div>

        {/* Timer */}
        <div className="text-center">
          <div className="text-4xl font-bold text-gray-900">
            {elapsed} min
          </div>
          <p className="text-sm text-gray-600">
            {format(new Date(order.created_at), 'HH:mm')}
          </p>
        </div>

        {/* Item Count */}
        <div className="text-center bg-white/50 rounded-lg py-2">
          <p className="text-lg font-semibold text-gray-800">
            {order.items?.reduce((sum: number, item: any) => sum + item.quantity, 0) || 0} article(s)
          </p>
        </div>
      </div>
    </Card>
  );
}
